/**
 * Turning a plan into orders, on the investor's behalf.
 *
 * Between "here is the plan" and "place it" sits a reconciliation the planner
 * cannot do for itself. `generate` picks funds without ever reading the order
 * gateway's rules for them, so every ISIN is looked up here and each order is
 * graded against what its scheme actually accepts — see
 * Helpers/portfolioPlanner/orderReconciliation.ts for the verdict, and the card
 * for the plan-versus-fund comparison it renders.
 *
 * ORDERS ARE PLACED ONE AT A TIME, IN A POPUP. Each one ends at a payment
 * gateway or a bank's e-mandate page, and neither can be framed — both send
 * X-Frame-Options: DENY, as a payment page must. A popup is the only way to
 * keep the plan on screen through that, and the plan has to stay: it lives in
 * memory, `generate` returns no id, and there is no endpoint to fetch it back.
 *
 * NO CONSENT OTP. The user platform gates each placement behind an OTP the
 * investor types. An admin acts directly on their behalf, so that layer is
 * absent — the provider's payment page and the bank's e-mandate authorization
 * are not part of it and are preserved exactly as the APIs require.
 *
 * DUPLICATE SAFETY. Once a line has created a purchase or a mandate at the
 * gateway, its ticket records that and the button will not create a second one.
 * What it offers instead is the step that cannot duplicate anything: for a SIP,
 * re-reading the mandate (which throws before creating a plan while the bank is
 * still approving); for a lumpsum, reconciling the payment against the purchase
 * that already exists.
 */

import React, { useMemo, useRef, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  finishPlannerLumpsum,
  placePlannerLumpsum,
  placePlannerSip,
  plannerPlacementError,
  resumePlannerSip,
} from "../../../Api/mfPlannerOrders";
import type { PlannerPlacementTicket } from "../../../Api/mfPlannerOrders";
import { isMFMandatePendingError } from "../../../Api/mfAdminInvestments";
import { openBlankPopup } from "../../../Helpers/mfPopupReturn";
import { mfFrequencyNeedsInstallmentDay } from "../../../Helpers/mfSchedule";
import { ensureMFPostbackBridge } from "../../../Helpers/mfPostbackBridge";
import { reconcileOrder } from "../../../Helpers/portfolioPlanner/orderReconciliation";
import type {
  OrderReconciliation,
  OrderSelection,
} from "../../../Helpers/portfolioPlanner/orderReconciliation";
import { usePlannerSchemes } from "../../../Helpers/portfolioPlanner/useSchemeRules";
import { rupee } from "../../../Helpers/portfolioPlanner/format";
import { plural } from "../../../Helpers/portfolioPlanner/report/format";
import type {
  PlanResult,
  PlannerOrder,
} from "../../../Helpers/portfolioPlanner/types";
import { InlineLoader } from "./PlannerFields";
import PlannerOrderCard from "./PlannerOrderCard";
import type { PlacementState } from "./PlannerOrderCard";
import styles from "./portfolio-planner.module.css";

const formatCurrency = (value: number): string => rupee(value);

const PlannerOrders = ({
  plan,
  userUuid,
  returnPath,
  placementBlockedReason,
  onBack,
  onPlaced,
}: {
  plan: PlanResult;
  userUuid: string;
  /** Route the popup returns to — validated by the /mf-payment-return bridge. */
  returnPath: string;
  /**
   * Set when no order may be placed for this investor at all. The screen still
   * reconciles every line — what the plan would place is worth knowing before
   * the onboarding is finished — it just cannot send any of it.
   */
  placementBlockedReason?: string;
  onBack: () => void;
  /** Lets the page refresh the investor's holdings and orders after a success. */
  onPlaced: () => void;
}) => {
  const orders = useMemo(() => plan.orders || [], [plan.orders]);
  const isins = useMemo(
    () =>
      orders
        .filter((order) => order.kind !== "bondLumpsum")
        .map((order) => order.isin),
    [orders]
  );
  const schemes = usePlannerSchemes(isins);

  const [selected, setSelected] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    orders.forEach((order) => {
      if (order.defaultSelected) initial[order.id] = true;
    });
    return initial;
  });
  const [choices, setChoices] = useState<Record<string, OrderSelection>>({});
  const [placements, setPlacements] = useState<Record<string, PlacementState>>({});
  const [busy, setBusy] = useState(false);
  const tickets = useRef<Record<string, PlannerPlacementTicket>>({});

  /* Tenure of the goal or loan an order funds — the lock-in check needs it, and
     only the plan knows it. */
  const monthsFor = useMemo(() => {
    const map: Record<string, { months: number | null; name: string }> = {};
    orders.forEach((order) => {
      const subject =
        order.source.type === "goal"
          ? plan.goals.find((goal) => goal.id === order.source.id)
          : plan.liabilities[order.source.id];
      map[order.id] = {
        months: subject && subject.totalMonths != null ? subject.totalMonths : null,
        name: order.source.name,
      };
    });
    return map;
  }, [orders, plan.goals, plan.liabilities]);

  const reconciliations = useMemo(() => {
    const map: Record<string, OrderReconciliation> = {};
    orders.forEach((order) => {
      const context = monthsFor[order.id];
      map[order.id] = reconcileOrder(
        order,
        order.kind === "bondLumpsum" ? undefined : schemes.byIsin[order.isin],
        {
          monthsToGoal: context ? context.months : null,
          goalName: context ? context.name : undefined,
        },
        choices[order.id] || {}
      );
    });
    return map;
  }, [orders, schemes.byIsin, choices, monthsFor]);

  const groups = useMemo(() => {
    const order: string[] = [];
    const map: Record<string, { title: string; rows: PlannerOrder[] }> = {};
    orders.forEach((row) => {
      const key = `${row.source.type}-${row.source.id}`;
      if (!map[key]) {
        map[key] = { title: row.source.name, rows: [] };
        order.push(key);
      }
      map[key].rows.push(row);
    });
    return order.map((key) => map[key]);
  }, [orders]);

  const tally = useMemo(() => {
    let ready = 0;
    let adjusted = 0;
    let choice = 0;
    let blocked = 0;
    let placedTotal = 0;
    let plannedTotal = 0;
    orders.forEach((order) => {
      const recon = reconciliations[order.id];
      if (!recon) return;
      if (recon.verdict === "ready") ready += 1;
      else if (recon.verdict === "adjusted") adjusted += 1;
      else if (recon.verdict === "needs-choice") choice += 1;
      else if (recon.verdict === "blocked" || recon.verdict === "unavailable") {
        blocked += 1;
      }
      if (recon.placeable && selected[order.id]) {
        placedTotal += recon.amount;
        plannedTotal += order.amount;
      }
    });
    return { ready, adjusted, choice, blocked, placedTotal, plannedTotal };
  }, [orders, reconciliations, selected]);

  const placedCount = Object.keys(placements).filter(
    (id) => placements[id].stage === "done"
  ).length;

  const toggle = (id: string) =>
    setSelected((current) => ({ ...current, [id]: !current[id] }));

  const patchChoice = (id: string, patch: OrderSelection) =>
    setChoices((current) => ({
      ...current,
      [id]: { ...(current[id] || {}), ...patch },
    }));

  const report = (id: string) => (progress: { stage: string; message: string }) =>
    setPlacements((current) => ({
      ...current,
      [id]: {
        ...(current[id] || {}),
        stage: progress.stage,
        message: progress.message,
        error: undefined,
      },
    }));

  const ticketFor = (id: string): PlannerPlacementTicket => {
    if (!tickets.current[id]) tickets.current[id] = {};
    return tickets.current[id];
  };

  const createdNoteFor = (ticket: PlannerPlacementTicket): string | undefined => {
    if (ticket.purchasePlanId) {
      return `SIP ${ticket.purchasePlanId} already exists for this line.`;
    }
    if (ticket.purchaseId) {
      return `Purchase ${ticket.purchaseId} was already created for this line, so it will not be created again. Reconcile the payment below, or cancel the purchase from the investor's Orders tab.`;
    }
    if (ticket.mandateId) {
      return `E-mandate ${ticket.mandateId} was already created for this line, so a second one will not be authorized.`;
    }
    return undefined;
  };

  const failPlacement = (
    id: string,
    error: unknown,
    ticket: PlannerPlacementTicket
  ) =>
    setPlacements((current) => ({
      ...current,
      [id]: {
        stage: "failed",
        message: "",
        error: plannerPlacementError(error),
        resumable: Boolean(ticket.resumable && ticket.mandateId),
        createdNote: createdNoteFor(ticket),
      },
    }));

  const succeed = (id: string, message: string, reference: string) => {
    setPlacements((current) => ({
      ...current,
      [id]: { stage: "done", message, reference },
    }));
    onPlaced();
  };

  const sipTargetFor = (order: PlannerOrder, recon: OrderReconciliation) => {
    const details = schemes.byIsin[order.isin];
    if (!details) throw new Error("The scheme rules for this order are not loaded.");
    return {
      userUuid,
      returnPath,
      schemeDetails: details,
      amount: recon.amount,
      frequency: recon.frequency || "monthly",
      installmentDay: mfFrequencyNeedsInstallmentDay(recon.frequency || "")
        ? recon.installmentDay || undefined
        : undefined,
      installments: recon.installments || 0,
    };
  };

  const place = async (order: PlannerOrder) => {
    if (placementBlockedReason) return;
    const recon = reconciliations[order.id];
    if (!recon || !recon.placeable || busy) return;
    const details = schemes.byIsin[order.isin];
    if (!details) return;
    const ticket = ticketFor(order.id);
    const isSip = order.kind === "mutualFundSip";

    // Opened inside the gesture, before any await — a popup opened after one is
    // blocked. It is pointed at the provider once the URL comes back.
    const popup = openBlankPopup(`smest_admin_mf_order_${order.id}`);
    if (!popup) {
      setPlacements((current) => ({
        ...current,
        [order.id]: {
          stage: "failed",
          message: "",
          error:
            "The browser blocked the authorization window. Allow pop-ups for this site and try this order again.",
          createdNote: createdNoteFor(ticket),
        },
      }));
      return;
    }

    setBusy(true);
    setPlacements((current) => ({
      ...current,
      [order.id]: { stage: "creating", message: "Preparing the return handler…" },
    }));

    try {
      // The provider may POST its callback. The service worker rewrites that to
      // a GET on the same route; without it the return lands on a blank page.
      await ensureMFPostbackBridge();

      if (isSip) {
        const result = await placePlannerSip(
          sipTargetFor(order, recon),
          popup,
          report(order.id),
          ticket
        );
        succeed(
          order.id,
          result.syncWarning
            ? "SIP confirmed. Its latest status could not be synchronized yet."
            : "SIP placed.",
          result.purchasePlanId
        );
      } else {
        const result = await placePlannerLumpsum(
          {
            userUuid,
            returnPath,
            schemeDetails: details,
            amount: recon.amount,
          },
          popup,
          report(order.id),
          ticket
        );
        succeed(order.id, "Lumpsum order placed.", result.purchaseId);
      }
    } catch (error) {
      failPlacement(order.id, error, ticket);
      if (!popup.closed) popup.close();
    } finally {
      setBusy(false);
    }
  };

  /**
   * Finishing a line that already created something, without creating a second
   * one.
   *
   * For a SIP that means re-running only the mandate verification, which throws
   * before it creates a purchase plan while the bank is still approving. For a
   * lumpsum it means reconciling the payment against the purchase that already
   * exists. Neither step can duplicate an order, which is exactly why these are
   * the only two retries the screen offers.
   */
  const resume = async (order: PlannerOrder) => {
    if (placementBlockedReason) return;
    const recon = reconciliations[order.id];
    if (!recon || busy) return;
    const ticket = ticketFor(order.id);
    const isSip = order.kind === "mutualFundSip";
    if (isSip ? !ticket.mandateId : !ticket.purchaseId) return;

    setBusy(true);
    setPlacements((current) => ({
      ...current,
      [order.id]: {
        stage: "confirming",
        message: isSip ? "Re-checking the mandate…" : "Reconciling the payment…",
      },
    }));
    try {
      if (isSip) {
        const result = await resumePlannerSip(
          sipTargetFor(order, recon),
          report(order.id),
          ticket
        );
        succeed(
          order.id,
          result.syncWarning
            ? "SIP confirmed. Its latest status could not be synchronized yet."
            : "SIP placed.",
          result.purchasePlanId
        );
      } else {
        const result = await finishPlannerLumpsum(report(order.id), ticket);
        succeed(order.id, "Lumpsum order placed.", result.purchaseId);
      }
    } catch (error) {
      setPlacements((current) => ({
        ...current,
        [order.id]: {
          stage: "failed",
          message: "",
          error: isMFMandatePendingError(error)
            ? `${plannerPlacementError(error)} Check again in a moment — nothing has been created for this line yet.`
            : plannerPlacementError(error),
          resumable: Boolean(
            ticket.resumable && (isSip ? ticket.mandateId : ticket.purchaseId)
          ),
          createdNote: createdNoteFor(ticket),
        },
      }));
    } finally {
      setBusy(false);
    }
  };

  if (!orders.length) {
    return (
      <div className={styles.wrap}>
        <button type="button" className={styles.ghost} onClick={onBack}>
          <ArrowBackIcon style={{ fontSize: 17 }} aria-hidden="true" />
          Back to the plan
        </button>
        <div className={styles.empty}>
          This plan does not imply any orders yet. Generate a plan with at least
          one funded goal to see what it would place.
        </div>
      </div>
    );
  }

  const rounding = Math.abs(tally.placedTotal - tally.plannedTotal);

  return (
    <div className={styles.wrap}>
      <header className={styles.ordersHead}>
        <span className={styles.kicker}>Place these orders</span>
        <h3>{plural(orders.length, "order", "orders")} follow from this plan</h3>
        <p>
          Each one is re-checked against what its scheme actually accepts before
          it can go — the amount band, the step, the SIP frequencies, the debit
          dates, the instalment minimum, the operation flags and the lock-in.
          Where the plan and the fund disagree, the difference is shown rather
          than applied quietly.
        </p>
      </header>

      {/* The state of the batch, in the vocabulary of the cards below it. */}
      <div className={styles.tally}>
        <div>
          <span>Ready</span>
          <b>{tally.ready}</b>
        </div>
        <div className={tally.adjusted ? styles.tallyWarn : undefined}>
          <span>Adjusted</span>
          <b>{tally.adjusted}</b>
        </div>
        <div className={tally.choice ? styles.tallyWarn : undefined}>
          <span>Needs a choice</span>
          <b>{tally.choice}</b>
        </div>
        <div className={tally.blocked ? styles.tallyBad : undefined}>
          <span>Blocked</span>
          <b>{tally.blocked}</b>
        </div>
        <div>
          <span>Placed</span>
          <b>
            {placedCount} of {orders.length}
          </b>
        </div>
      </div>

      {placementBlockedReason ? (
        <p className={`${styles.callout} ${styles.calloutWarn}`}>
          <WarningAmberIcon style={{ fontSize: 16, flex: "0 0 auto" }} aria-hidden="true" />
          <span>{placementBlockedReason}</span>
        </p>
      ) : null}

      {schemes.isLoading ? (
        <p className={styles.callout}>
          <InlineLoader />
          <span>
            Reading scheme rules — {schemes.loaded} of {schemes.total} loaded.
            Each fund publishes its own amount steps, SIP frequencies and debit
            dates, and none of them are in the plan.
          </span>
        </p>
      ) : null}

      {schemes.failed.length ? (
        <p className={`${styles.callout} ${styles.calloutWarn}`}>
          <WarningAmberIcon style={{ fontSize: 16, flex: "0 0 auto" }} aria-hidden="true" />
          <span>
            {plural(schemes.failed.length, "scheme", "schemes")} could not be
            loaded, so those orders cannot be validated and will not be placed.
            <button
              type="button"
              className={styles.ghost}
              style={{ marginLeft: 10 }}
              disabled={schemes.isLoading}
              aria-busy={schemes.isLoading || undefined}
              onClick={schemes.retryFailed}
            >
              {schemes.isLoading ? <InlineLoader /> : <RefreshIcon style={{ fontSize: 16 }} />}
              Retry those lookups
            </button>
          </span>
        </p>
      ) : null}

      {rounding >= 0.01 ? (
        <p className={`${styles.callout} ${styles.calloutWarn}`}>
          <WarningAmberIcon style={{ fontSize: 16, flex: "0 0 auto" }} aria-hidden="true" />
          <span>
            Rounding to each scheme&apos;s step moves the selected total from{" "}
            {formatCurrency(tally.plannedTotal)} to{" "}
            {formatCurrency(tally.placedTotal)}. The downloaded report quotes the
            plan&apos;s figure, so the two will differ by{" "}
            {formatCurrency(rounding)}.
          </span>
        </p>
      ) : null}

      {groups.map((group) => (
        <section key={group.title} className={styles.group}>
          <h4 className={styles.groupTitle}>{group.title}</h4>
          <ul className={styles.orderList}>
            {group.rows.map((order) => {
              const recon = reconciliations[order.id];
              if (!recon) return null;
              return (
                <PlannerOrderCard
                  key={order.id}
                  order={order}
                  reconciliation={recon}
                  selection={choices[order.id] || {}}
                  placement={placements[order.id]}
                  checked={Boolean(selected[order.id])}
                  busy={busy}
                  onToggle={() => toggle(order.id)}
                  onSelect={(patch) => patchChoice(order.id, patch)}
                  onPlace={() => {
                    place(order);
                  }}
                  onResume={() => {
                    resume(order);
                  }}
                  resumeLabel={
                    order.kind === "mutualFundSip"
                      ? "Resume after bank approval"
                      : "Reconcile the payment"
                  }
                  placementBlockedReason={placementBlockedReason}
                />
              );
            })}
          </ul>
        </section>
      ))}

      <p className={styles.callout}>
        <InfoOutlinedIcon style={{ fontSize: 16, flex: "0 0 auto" }} aria-hidden="true" />
        <span>
          Orders are placed one at a time. Each opens a payment or bank
          authorization window — those pages cannot be embedded, so a separate
          window is the only way to keep this plan on screen while it is
          authorized. No consent OTP is sent; the admin is acting on the
          investor&apos;s behalf.
        </span>
      </p>

      {placedCount ? (
        <p className={`${styles.callout} ${styles.calloutGood}`}>
          <CheckCircleIcon style={{ fontSize: 16, flex: "0 0 auto" }} aria-hidden="true" />
          <span>
            {plural(placedCount, "order", "orders")} created. Creating an order
            does not fund it — each placed SIP still needs its mandate active,
            and each one-time investment still needs its payment cleared. Both
            show up on the investor&apos;s Orders tab.
          </span>
        </p>
      ) : null}

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.ghost}
          onClick={onBack}
          disabled={busy}
        >
          <ArrowBackIcon style={{ fontSize: 17 }} aria-hidden="true" />
          Back to the plan
        </button>
      </div>
    </div>
  );
};

export default PlannerOrders;
